/**
 * LIGHT STYLES — the 3D bodies (2026-10-07, Matt: "We're going to want the fixture geometry to
 * look nice … some reasonable level of detail so they don't look like clunky blocks" and "make
 * sure these fixtures capture their intended character with the right level of detail" — the
 * gas meter set the bar). One builder per style in the kernel catalog (kernel
 * categories/fixture/lightStyles.ts — names, default finishes, the extents the pick box and z
 * span read, the bulb the walkthrough light sits at).
 *
 * Built at REAL SIZE in inches, scaled to feet, from a small kit:
 *   lathe   a surface of revolution from an (r, z) profile — shades, domes, globes, cups,
 *           finials; at 4 / 8 segments (flat shaded) it is a pyramid roof or an octagon
 *   tube    a round (or square) section swept along a 3D path — gooseneck arms, scrolls,
 *           candle arms, cage ribs, rings
 *   bar     a square bar between two points — lantern posts, rails, muntins
 *   pane    a glass pane (both faces) — lantern glazing
 *   chain   oval links — chandelier and lantern chains
 * under a frame stack (rotate / translate), so a repeated arm is written once.
 *
 * MATERIAL ROLES (each becomes a solidGroup): 'metal' — the brushable body, its color the
 * instance's brushed color / project standard (el.memberColor) else the style's finish, over
 * the shared Light Fixture material; 'enamel' (a shade's white inside), 'opal' (milk glass),
 * 'fabric' (a shade), 'candle' (sleeves), 'bulb', 'cord', 'wood' — fixed looks. Clear glass
 * goes to the proto's glass buffer.
 *
 * FRAMES (lightStyles.ts): wall kinds x along the wall, y off the face, z about the mount
 * point; ceiling kinds z = 0 the ceiling, hanging down; ground kinds z = 0 the base.
 */
import type { ParamValue, ResolvedElement } from '@aj/bim-kernel'
import {
  lightStyleOf, lightDropFt, lightBodyMaterialId, tintMaterialId, type LightStyle,
} from '@aj/bim-kernel'
import { MeshBuilder } from '../../extrude'
import { LB, ID, add, mul, rotX, type V3 } from '../../meshKit'
import { featureEdges } from '../../edges'
import type { OpeningProto } from '../opening/protos'
import { ceilingTiltOf, tiltForCeiling } from './ceilingTilt'

/** fixed looks (the brushable 'metal' resolves per instance) */
const ROLE_HEX: Record<string, string> = {
  enamel: '#efeee9', opal: '#f3f0e6', fabric: '#e8e0cc', candle: '#f2ecdc', bulb: '#fff1c4',
  cord: '#1b1a19', wood: '#5b4836', ceramic: '#e6e1d4',
}


// ======================================================================================
// shared pieces
// ======================================================================================

/** a candelabra candle: sleeve, then the flame bulb (z0 = the sleeve's foot) */
function candle(L: LB, c: V3, h = 4, r = 0.55) {
  L.role('candle').latheAt(c, [[0, h], [r, h], [r, 0], [0, 0]], 12)
  L.role('bulb').latheAt(add(c, [0, 0, h]), [[0, 1.9], [0.25, 1.75], [0.55, 1.1], [0.5, 0.45], [0.2, 0.05], [0, 0]], 12)
}
/** a bobeche (drip cup) with its socket cup, sitting on z */
function bobeche(L: LB, c: V3, r = 1.7) {
  L.role('metal').latheAt(c, [[0, 0.55], [0.8, 0.55], [0.85, 0.35], [r, 0.3], [r + 0.1, 0.12], [r * 0.9, -0.05], [0.6, -0.3], [0.4, -0.9], [0, -0.9]], 20)
}
/** a pear / A19 bulb and its socket, the bulb pointing down from z (base up) */
/** a pear bulb's profile, base (z 0) to tip (z −b): its radius along the length */
const PEAR: [number, number][] = [[0.55, 0], [0.6, 0.12], [1.05, 0.33], [1.25, 0.55], [1.12, 0.76], [0.65, 0.94], [0, 1]]
function edisonDown(L: LB, c: V3, len = 5.2) {
  const b = Math.max(1.5, len - 1.6)
  L.role('metal').latheAt(c, [[0, 0], [0.8, 0], [0.8, -1.4], [0.6, -1.6], [0, -1.6]], 14)
  L.role('bulb').latheAt(add(c, [0, 0, -1.6]), [[0, 0], ...PEAR.map(([r, f]) => [r, -f * b] as [number, number])], 14)
}
/** the same, standing up from z */
function edisonUp(L: LB, c: V3, len = 5.2) {
  const b = Math.max(1.5, len - 1.6)
  L.role('metal').latheAt(c, [[0, 1.6], [0.6, 1.6], [0.8, 1.4], [0.8, 0], [0, 0]], 14)
  L.role('bulb').latheAt(add(c, [0, 0, 1.6]), [...PEAR.slice().reverse().map(([r, f]) => [r, f * b] as [number, number]), [0, 0]], 14)
}
/** a small ball finial with its neck, standing on z */
function finial(L: LB, c: V3, ball = 0.8, spike = 1.4) {
  L.role('metal').latheAt(c, [[0, 0.6 + ball * 2 + spike], [0.12, 0.6 + ball * 2 + 0.2], [ball * 0.35, 0.6 + ball * 2],
    [ball, 0.6 + ball], [ball * 0.35, 0.6], [0.35, 0.45], [0.55, 0.15], [0.55, 0], [0, 0]], 16)
}
/** a ceiling canopy (z = 0 the ceiling) */
function canopy(L: LB, r = 2.6, h = 1.1, square = false) {
  L.role('metal')
  if (square) L.lathe([[0, 0], [r * Math.SQRT2, 0], [r * Math.SQRT2, -h * 0.7], [r * Math.SQRT2 * 0.8, -h], [0, -h]], 4, Math.PI / 4)
  else L.lathe([[0, 0], [r, 0], [r, -h * 0.35], [r * 0.85, -h * 0.9], [r * 0.45, -h], [0, -h]], 24)
}
/** a downrod from the canopy to z */
function downrod(L: LB, z: number, r = 0.42) {
  L.role('metal').cyl([0, 0, -1], [0, 0, z], r, 12)
}

/**
 * A glazed lantern body: n = 4 sides, from zb (half-width hb) to zt (half-width ht), corner
 * posts, top + bottom rails, glass in every side (or `open` sides left out — the wall side of a
 * wall lantern carries a metal back instead), optional muntins (cols × rows per side).
 */
function lanternBody(L: LB, o: {
  zb: number; zt: number; hb: number; ht: number; post?: number; rail?: number
  cols?: number; rows?: number; backY?: number | null; noGlassSide?: number
}) {
  const post = o.post ?? 0.32, rail = o.rail ?? 0.45
  const cor = (h: number, z: number): V3[] => [[-h, -h, z], [h, -h, z], [h, h, z], [-h, h, z]]
  const B = cor(o.hb, o.zb), T = cor(o.ht, o.zt)
  L.role('metal')
  for (let i = 0; i < 4; i++) L.bar(B[i], T[i], post)
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4
    L.bar(add(B[i], [0, 0, rail / 2]), add(B[j], [0, 0, rail / 2]), rail / 2)
    L.bar(add(T[i], [0, 0, -rail / 2]), add(T[j], [0, 0, -rail / 2]), rail / 2)
    // muntins on this side
    const cols = o.cols ?? 1, rows = o.rows ?? 1
    for (let c = 1; c < cols; c++) {
      const f = c / cols
      L.bar(add(mul(B[i], 1 - f), mul(B[j], f)), add(mul(T[i], 1 - f), mul(T[j], f)), 0.12)
    }
    for (let r = 1; r < rows; r++) {
      const f = r / rows
      const a = add(mul(B[i], 1 - f), mul(T[i], f)), b = add(mul(B[j], 1 - f), mul(T[j], f))
      L.bar(a, b, 0.12)
    }
    if (i === o.noGlassSide) continue
    const inset = 0.05
    const k = (p: V3, h: number): V3 => [p[0] * (1 - inset / Math.max(h, 1)), p[1] * (1 - inset / Math.max(h, 1)), p[2]]
    L.pane(k(B[i], o.hb), k(B[j], o.hb), k(T[j], o.ht), k(T[i], o.ht))
  }
  if (o.noGlassSide !== undefined) {
    // the solid back (the wall side) — a thin metal panel inside the posts
    const i = o.noGlassSide, j = (i + 1) % 4
    L.role('metal').quad(B[j], B[i], T[i], T[j]); L.quad(B[i], B[j], T[j], T[i])
  }
}

/** a hipped lantern roof: the eave plate at z (half-width e), the pyramid to `apex` */
function hipRoof(L: LB, z: number, e: number, rise: number, top = 0.9, flare = false) {
  L.role('metal')
  const R = e * Math.SQRT2
  // eave plate
  L.lathe([[0, z + 0.45], [R, z + 0.45], [R, z], [0, z]], 4, Math.PI / 4)
  if (flare) {
    // pagoda: a concave roof that kicks up at the eave
    // a concave sweep: nearly flat at the eave, steep at the top, the eave tip kicked up
    const pr: [number, number][] = []
    const n = 10
    const Re = R * 1.2
    for (let i = 0; i <= n; i++) {
      const t = i / n
      const r = Re * (1 - t) + top * Math.SQRT2 * t
      const zz = z + 0.45 + rise * Math.pow(t, 2.3) + (i === 0 ? 0.7 : 0)
      pr.push([r, zz])
    }
    pr.reverse()
    L.lathe([[0, pr[0][1]], ...pr, [Re, z + 0.2], [R * 0.9, z + 0.45], [0, z + 0.45]], 4, Math.PI / 4)
  } else {
    L.lathe([[0, z + 0.45 + rise], [top * Math.SQRT2, z + 0.45 + rise], [R * 0.98, z + 0.5], [0, z + 0.5]], 4, Math.PI / 4)
  }
}

// ======================================================================================
// WALL SCONCES (mount point = z 0 on the wall face, y off the wall)
// ======================================================================================

/** the gooseneck arm + round backplate of a barn light, ending pointing down at (0, 17, 5.2) */
function gooseneck(L: LB) {
  L.role('metal')
  // the round wall plate (a cast canopy) and its collar
  L.latheAt([0, 0, 0], [[0, 0.75], [2.4, 0.75], [2.75, 0.45], [2.75, 0], [0, 0]], 28, 0, rotX(-Math.PI / 2))
  L.latheAt([0, 0.75, 0], [[0, 1.1], [0.95, 1.1], [0.95, 0], [0, 0]], 14, 0, rotX(-Math.PI / 2))
  // the swan neck: out of the wall, up over the top, and down into the shade
  const pts = LB.bez([0, 1.6, 0], [0, 11, 0], [0, 17.2, 12.5], [0, 17, 5.6], 22)
  L.tube(pts, 0.5, 12)
}
function sconceBarn(L: LB, cone: boolean) {
  gooseneck(L)
  // the socket housing on the arm's end
  L.role('metal').latheAt([0, 17, 0], [[0, 5.8], [0.62, 5.8], [1.1, 5.2], [1.15, 3.0], [1.35, 2.7], [0, 2.7]], 18)
  const prof: [number, number][] = cone
    ? [[1.3, 2.75], [2.0, 2.55], [4.0, 0.35], [5.9, -2.6], [6.5, -3.9], [6.7, -4.3], [6.6, -4.45], [6.42, -4.2], [5.8, -2.85], [3.9, 0.15], [1.9, 2.35], [1.2, 2.5]]
    : [[1.3, 2.75], [3.0, 2.5], [4.8, 1.75], [6.0, 0.4], [6.8, -1.6], [7.1, -3.2], [7.2, -3.75], [7.1, -3.9], [6.95, -3.7], [6.68, -1.65], [5.88, 0.3], [4.7, 1.62], [2.95, 2.36], [1.2, 2.5]]
  // shade: the outside metal, the white enamel inside (split along the profile)
  const half = Math.floor(prof.length / 2)
  L.role('metal').latheAt([0, 17, 0], prof.slice(0, half + 1), 32)
  L.role('enamel').latheAt([0, 17, 0], prof.slice(half), 32)
  // the rolled rim bead
  L.role('metal').ring([0, 17, cone ? -4.35 : -3.82], cone ? 6.6 : 7.12, 0.14, 'z', 40, 6)
  edisonDown(L, [0, 17, 2.6], 4.6)
}

/** a coach lantern on a scroll bracket: lantern centre 6.6 off the wall */
function sconceCoach(L: LB) {
  const cy = 6.6
  // the tall backplate with chamfered ends
  L.role('metal')
  L.box(-1.9, 1.9, 0, 0.55, -10.5, 10.5)
  L.box(-1.5, 1.5, 0.55, 0.85, -9.6, 9.6)
  // the bottom scroll bracket carrying the lantern, and the top tie
  L.tube(LB.bez([0, 0.8, -8.2], [0, 4, -9.4], [0, cy, -9.6], [0, cy, -7.6], 14), 0.38, 4)
  L.tube(LB.bez([0, 0.8, -4.8], [0, 2.5, -4.6], [0, 3.6, -7.4], [0, 5.2, -7.9], 10), 0.26, 4)
  L.tube(LB.bez([0, 0.8, 8.4], [0, 3.0, 8.4], [0, 5.0, 9.4], [0, cy, 10.8], 10), 0.3, 4)
  L.frame([0, cy, 0], ID, () => {
    // the bottom pan and its drop finial
    L.role('metal')
    L.lathe([[0, -6.0], [3.6 * Math.SQRT2, -6.0], [3.6 * Math.SQRT2, -6.45], [0.8 * Math.SQRT2, -7.7], [0, -7.7]], 4, Math.PI / 4)
    L.latheAt([0, 0, -7.7], [[0, 0], [0.4, 0], [0.75, -0.6], [0.3, -1.4], [0.08, -2.2], [0, -2.4]], 14)
    lanternBody(L, { zb: -6.0, zt: 6.0, hb: 3.1, ht: 4.0, post: 0.3, noGlassSide: 0 })
    hipRoof(L, 6.0, 4.6, 3.8, 1.25)
    // chimney cap and finial
    L.role('metal').latheAt([0, 0, 10.2], [[0, 1.3], [1.6 * Math.SQRT2, 1.3], [1.6 * Math.SQRT2, 0.95], [1.15 * Math.SQRT2, 0.95], [1.15 * Math.SQRT2, 0], [0, 0]], 4, Math.PI / 4)
    finial(L, [0, 0, 11.5], 0.65, 1.4)
    // the candle inside
    L.role('metal').latheAt([0, 0, -6.0], [[0, 1.2], [1.1, 1.2], [1.4, 0.4], [1.4, 0], [0, 0]], 16)
    candle(L, [0, 0, -4.8], 3.6, 0.55)
  })
}

/** a tall gas lantern hung from a strap bracket (the Newport / French Quarter wall lantern) */
function sconceGas(L: LB) {
  const cy = 8.3
  L.role('metal')
  L.box(-2.0, 2.0, 0, 0.6, -9.0, 15.0)                                       // wall plate
  // the strap arm at the top with its scroll brace, and the hanging yoke
  L.box(-0.32, 0.32, 0.6, cy + 0.6, 15.2, 16.0)
  L.tube(LB.bez([0, 0.6, 8.6], [0, 3.5, 9.2], [0, 7.0, 13.6], [0, cy - 1.5, 15.2], 14), 0.3, 4)
  L.tube(LB.bez([0, 3.0, 15.2], [0, 3.2, 13.4], [0, 4.6, 13.0], [0, 5.4, 14.1], 10), 0.2, 4)
  L.ring([0, cy, 16.6], 0.95, 0.18, 'y', 16, 6)                                // the hook ring
  L.frame([0, cy, 0], ID, () => {
    L.role('metal')
    // bottom: a square cup tapering to a ball drop
    L.lathe([[0, -9.6], [3.5 * Math.SQRT2, -9.6], [3.5 * Math.SQRT2, -10.1], [1.2 * Math.SQRT2, -12.6], [0, -12.6]], 4, Math.PI / 4)
    L.latheAt([0, 0, -12.6], [[0, 0], [0.5, 0], [0.9, -0.9], [0.5, -1.8], [0, -2.0]], 14)
    lanternBody(L, { zb: -9.6, zt: 9.4, hb: 3.0, ht: 4.4, post: 0.34, rows: 1, noGlassSide: 0 })
    // the burner gallery and the gas flame
    L.role('metal').latheAt([0, 0, -9.6], [[0, 3.2], [0.45, 3.2], [0.45, 0.6], [1.6, 0.6], [1.6, 0], [0, 0]], 16)
    L.role('bulb').latheAt([0, 0, -6.4], [[0, 4.6], [0.3, 4.1], [0.95, 2.6], [1.1, 1.2], [0.7, 0.2], [0, 0]], 14)
    // roof: a flared hip with a vent chimney, its cap and the hanging loop
    hipRoof(L, 9.4, 5.2, 3.2, 1.6)
    L.role('metal').latheAt([0, 0, 13.0], [[0, 1.5], [1.25, 1.5], [1.25, 0], [0, 0]], 16)
    L.latheAt([0, 0, 14.5], [[0, 0.5], [2.1, 0.5], [2.3, 0.2], [2.1, 0], [0, 0]], 20)
    L.ring([0, 0, 16.6], 0.95, 0.2, 'x', 16, 6)
  })
}

/** a modern box lantern: flat backplate, a crisp rectangle of thin frame and glass, flat cap */
function sconceBox(L: LB) {
  const y0 = 1.0, y1 = 7.3, h = 7.0
  L.role('metal')
  L.box(-2.6, 2.6, 0, 0.75, -6.4, 6.4)                                       // backplate
  L.box(-0.9, 0.9, 0.75, 1.05, 3.6, 5.2)                                      // the arm stub
  // the frame: 4 posts, top + bottom rectangles
  const xs = [-3.5, 3.5], ys = [y0, y1]
  for (const x of xs) for (const y of ys) L.box(x - 0.2, x + 0.2, y - 0.2, y + 0.2, -h, h)
  for (const z of [-h, h - 0.35]) {
    L.box(-3.7, 3.7, y0 - 0.2, y0 + 0.2, z, z + 0.35); L.box(-3.7, 3.7, y1 - 0.2, y1 + 0.2, z, z + 0.35)
    L.box(-3.7, -3.3, y0, y1, z, z + 0.35); L.box(3.3, 3.7, y0, y1, z, z + 0.35)
  }
  // the flat overhanging cap and the thin bottom plate
  L.box(-3.9, 3.9, y0 - 0.4, y1 + 0.3, h, h + 0.55)
  L.box(-3.3, 3.3, y0 + 0.2, y1 - 0.2, -h - 0.1, -h + 0.15)
  // glass on three sides; a metal panel at the back
  L.pane([-3.3, y1, -h], [3.3, y1, -h], [3.3, y1, h], [-3.3, y1, h])
  L.pane([3.5, y0, -h], [3.5, y1, -h], [3.5, y1, h], [3.5, y0, h])
  L.pane([-3.5, y1, -h], [-3.5, y0, -h], [-3.5, y0, h], [-3.5, y1, h])
  L.role('metal').box(-3.3, 3.3, y0 - 0.05, y0 + 0.05, -h, h)
  edisonDown(L, [0, (y0 + y1) / 2, h], 5.6)
}

/** a ship's (bulkhead) cage light: cast plate, curved arm, dome hood, caged globe */
function cageLamp(L: LB, c: V3, opts: { hoodR?: number } = {}) {
  const hr = opts.hoodR ?? 6.1
  L.frame(c, ID, () => {
    L.role('metal')
    // the fitter neck and the dome hood (enamel inside)
    L.lathe([[0, 2.0], [1.3, 2.0], [1.3, 1.1], [0, 1.1]], 16)
    L.lathe([[1.25, 1.2], [2.8, 1.0], [4.6, 0.45], [hr - 0.2, -0.2], [hr, -0.5], [hr - 0.15, -0.65]], 32)
    L.role('enamel').lathe([[hr - 0.15, -0.65], [4.5, 0.25], [2.7, 0.8], [1.25, 1.0]], 32)
    // the globe's threaded collar, the globe, the cage
    L.role('metal').lathe([[0, -0.4], [2.3, -0.4], [2.45, -0.8], [2.45, -1.4], [2.2, -1.5], [0, -1.5]], 24)
    L.role('glass').sphere([0, 0, -4.4], 3.05, 20)
    L.role('metal')
    const R = 3.35
    L.ring([0, 0, -4.4], R, 0.16, 'z', 28, 6)
    L.ring([0, 0, -2.6], 2.75, 0.14, 'z', 24, 6)
    L.ring([0, 0, -6.2], 2.75, 0.14, 'z', 24, 6)
    for (let k = 0; k < 4; k++) {
      const a = (Math.PI / 2) * k + Math.PI / 4
      const pts: V3[] = []
      for (let i = 0; i <= 12; i++) {
        const t = -Math.PI / 2 + (Math.PI * i) / 12
        const rr = Math.cos(t) * R, zz = -4.4 + Math.sin(t) * R
        if (zz > -1.4) continue
        pts.push([Math.cos(a) * Math.max(rr, 0.9), Math.sin(a) * Math.max(rr, 0.9), zz])
      }
      L.tube(pts, 0.14, 6, false)
    }
    L.latheAt([0, 0, -7.6], [[0, 0.25], [1.0, 0.25], [1.0, -0.2], [0.4, -0.55], [0, -0.55]], 14)
    edisonDown(L, [0, 0, -1.2], 4.4)
  })
}
function sconceBulkhead(L: LB) {
  L.role('metal')
  // the cast round plate, its boss, the arm out and down into the hood's fitter
  L.latheAt([0, 0, 0], [[0, 0.9], [2.5, 0.9], [3.0, 0.5], [3.0, 0], [0, 0]], 28, 0, rotX(-Math.PI / 2))
  L.latheAt([0, 0.9, 0], [[0, 1.0], [1.0, 1.0], [1.15, 0], [0, 0]], 16, 0, rotX(-Math.PI / 2))
  L.tube(LB.bez([0, 1.8, 0], [0, 5.0, 0.2], [0, 7.0, 2.4], [0, 7.0, 4.0], 12).reverse(), 0.55, 12)
  L.latheAt([0, 7, 2.0], [[0, 2.4], [0.75, 2.4], [0.85, 2.0], [0.85, 0], [0, 0]], 14)
  cageLamp(L, [0, 7, 0])
}

/** a swing-arm library sconce with an empire fabric shade */
function sconceLibrary(L: LB) {
  L.role('metal')
  L.latheAt([0, 0, 0], [[0, 0.7], [2.1, 0.7], [2.5, 0.45], [2.5, 0], [0, 0]], 28, 0, rotX(-Math.PI / 2))
  // the swivel knuckle and the arm
  L.cyl([0, 0.7, 0], [0, 1.6, 0], 0.5, 12)
  L.cyl([0, 1.9, -1.4], [0, 1.9, 1.4], 0.55, 14)
  L.latheAt([0, 1.9, 1.4], [[0, 0.5], [0.3, 0.5], [0.55, 0], [0, 0]], 12)
  L.tube([[0, 1.9, 0.6], [0, 10.4, 0.6], [0, 11, 0.9], [0, 11, 2.2]], 0.32, 10)
  // the socket cup, the harp and finial
  L.latheAt([0, 11, 2.0], [[0, 2.3], [0.7, 2.3], [0.85, 1.6], [0.95, 0.4], [0.5, 0], [0, 0]], 16)
  for (const s of [-1, 1]) L.tube([[s * 0.6, 11, 3.6], [s * 2.0, 11, 6.2], [s * 0.25, 11, 9.4]], 0.09, 6)
  finial(L, [0, 11, 9.4], 0.45, 0.4)
  // the bulb
  edisonUp(L, [0, 11, 4.0], 3.0)
  // the empire shade: fabric, a thin shell (outside, then the inside), its wire rims
  L.role('fabric').latheAt([0, 11, 0], [[2.8, 9.6], [4.8, 3.6], [4.72, 3.6], [2.73, 9.6]], 36)
  L.role('metal').ring([0, 11, 9.6], 2.8, 0.06, 'z', 30, 4)
  L.ring([0, 11, 3.6], 4.8, 0.06, 'z', 36, 4)
}

/** CASEWORK STUDIO stage 4: a picture light — round plate, a swept arm up and out, a long
 *  half-round hood over a lamp strip, lighting the art below it */
function sconcePicture(L: LB) {
  L.role('metal')
  L.latheAt([0, 0, 0], [[0, 0.5], [1.5, 0.5], [1.7, 0.3], [1.7, 0], [0, 0]], 24, 0, rotX(-Math.PI / 2))
  L.tube([[0, 0.5, 0], [0, 3.0, 0.4], [0, 5.6, 2.0], [0, 6.8, 3.9]], 0.28, 10)
  // the hood: a long half-round shell, open below, its two end caps
  L.cyl([-9, 7.0, 4.6], [9, 7.0, 4.6], 1.4, 18)
  for (const x of [-9.2, 9.2]) L.cyl([x - 0.15, 7.0, 4.6], [x + 0.15, 7.0, 4.6], 1.55, 18)
  // the lamp strip under it
  L.role('bulb').box(-8.6, 8.6, 6.6, 7.4, 3.0, 3.35)
}

/** a plaster up/down half bowl (the Spanish / Mediterranean wall sconce) */
function sconceHalfBowl(L: LB) {
  // the half bowl: outside down and round, the inside back up, closed to the wall by a flat back
  const out: [number, number][] = []
  for (let i = 0; i <= 10; i++) {
    const a = (Math.PI / 2) * (i / 10)
    out.push([5.3 * Math.cos(a) + 0.2, 4.0 - 9.2 * Math.sin(a)])
  }
  const prof: [number, number][] = [[5.0, 4.4], [5.55, 4.4], ...out.map(([r, z]) => [r, Math.min(z, 4.0)] as [number, number])]
  prof.push([0, -5.3])
  L.role('metal')
  L.frame([0, 0, 0], ID, () => L.lathe(prof, 20, 0, 0, Math.PI))
  // the inside of the bowl (open top)
  const inner: [number, number][] = [[0, -3.9], ...out.slice().reverse().map(([r, z]) => [Math.max(0, r - 0.6), Math.min(z, 4.0) + 0.6] as [number, number])]
  inner.push([5.0, 4.4])
  L.role('enamel').frame([0, 0, 0], ID, () => L.lathe(inner, 20, 0, 0, Math.PI))
  // the wall-side back: the bowl's own outline, flat against the wall
  const outline: [number, number][] = [[-5.55, 4.4], ...out.map(([r, z]) => [-r, z] as [number, number]), [0, -5.3],
    ...out.slice().reverse().map(([r, z]) => [r, z] as [number, number]), [5.55, 4.4]]
  L.role('metal').plateY(outline, 0, 0.3)
  edisonUp(L, [0, 2.6, -0.3], 3.6)
}

// ======================================================================================
// STEP LIGHTS (mount point = the faceplate centre)
// ======================================================================================

function stepLouver(L: LB) {
  L.role('metal')
  L.box(-3.3, 3.3, 0, 0.35, -1.6, 1.6)                                       // faceplate
  L.box(-3.3, 3.3, 0.35, 0.55, 1.35, 1.6)
  // the angled louver blades over a glowing lens
  for (const z of [0.8, -0.05, -0.9]) {
    L.frame([0, 0.35, z], rotX(-0.55), () => L.box(-2.7, 2.7, 0, 0.95, -0.12, 0.12))
  }
  L.box(-2.9, -2.7, 0.35, 1.2, -1.3, 1.3); L.box(2.7, 2.9, 0.35, 1.2, -1.3, 1.3)
  L.role('bulb').box(-2.6, 2.6, 0.05, 0.3, -1.3, 1.2)
}
function stepEyelid(L: LB) {
  L.role('metal')
  // the round faceplate (lathe about y), and the eyelid hood over its upper half
  L.latheAt([0, 0, 0], [[0, 0.3], [2.0, 0.3], [2.2, 0.15], [2.2, 0], [0, 0]], 28, 0, rotX(-Math.PI / 2))
  const hood: [number, number][] = []
  for (let i = 0; i <= 8; i++) { const a = (Math.PI / 2) * (i / 8); hood.push([1.9 * Math.cos(a), 0.3 + 1.3 * Math.sin(a)]) }
  L.frame([0, 0, 0], rotX(-Math.PI / 2), () => L.lathe([[0, 1.6], ...hood.slice().reverse(), [1.9, 0.3]], 20, 0, Math.PI, Math.PI * 2))
  L.role('bulb').latheAt([0, 0.31, -0.5], [[0, 0.05], [1.1, 0.05], [1.1, 0], [0, 0]], 16, 0, rotX(-Math.PI / 2))
}

// ======================================================================================
// PENDANTS (z 0 = the ceiling; zb = the fixture's bottom)
// ======================================================================================

function pendantWarehouse(L: LB, zb: number) {
  canopy(L); downrod(L, zb + 8.4)
  L.role('metal').latheAt([0, 0, zb], [[0, 9.6], [0.9, 9.6], [1.3, 8.4], [1.35, 7.3], [0, 7.3]], 16)
  const prof: [number, number][] = [[1.3, 7.3], [2.6, 7.0], [4.6, 5.8], [6.6, 3.6], [7.8, 1.6], [8.3, 0.25], [8.3, 0], [8.05, 0.12], [7.55, 1.55], [6.4, 3.45], [4.5, 5.55], [2.55, 6.75], [1.25, 7.0]]
  L.role('metal').latheAt([0, 0, zb], prof.slice(0, 7), 36)
  L.role('enamel').latheAt([0, 0, zb], prof.slice(6), 36)
  L.role('metal').ring([0, 0, zb + 0.1], 8.2, 0.12, 'z', 40, 6)
  edisonDown(L, [0, 0, zb + 7.3], 4.6)
}

function pendantLantern(L: LB, zb: number) {
  canopy(L)
  L.role('metal').chain([0, 0, -1.1], [0, 0, zb + 29.4])
  L.ring([0, 0, zb + 29.0], 0.8, 0.18, 'x', 14, 6)
  L.cyl([0, 0, zb + 27.5], [0, 0, zb + 28.3], 0.3, 8)
  L.frame([0, 0, zb + 3.8], ID, () => {
    L.role('metal')
    // bottom pan + drop finial
    L.lathe([[0, 0], [5.0 * Math.SQRT2, 0], [5.0 * Math.SQRT2, -0.55], [1.2 * Math.SQRT2, -2.2], [0, -2.2]], 4, Math.PI / 4)
    L.latheAt([0, 0, -2.2], [[0, 0], [0.45, 0], [0.8, -0.7], [0.3, -1.4], [0, -1.6]], 14)
    lanternBody(L, { zb: 0, zt: 17.5, hb: 4.6, ht: 5.8, post: 0.36, rows: 1 })
    hipRoof(L, 17.5, 6.6, 4.4, 1.5)
    L.role('metal').latheAt([0, 0, 22.3], [[0, 1.6], [1.9, 1.6], [1.9, 1.2], [1.3, 1.2], [1.3, 0], [0, 0]], 4, Math.PI / 4)
    // a cluster of three candles on a cross
    L.latheAt([0, 0, 0], [[0, 1.5], [0.55, 1.5], [0.55, 0], [0, 0]], 12)
    for (let k = 0; k < 3; k++) {
      const a = (Math.PI * 2 * k) / 3
      const p: V3 = [Math.cos(a) * 2.2, Math.sin(a) * 2.2, 4.2]
      L.role('metal').tube([[0, 0, 1.3], [Math.cos(a) * 1.4, Math.sin(a) * 1.4, 2.0], [p[0], p[1], 3.4], p], 0.2, 6)
      bobeche(L, p, 1.0)
      candle(L, p, 3.6, 0.45)
    }
  })
}

function pendantOctagon(L: LB, zbIn: number) {
  const zb = zbIn + 1.6           // the hanging bud reaches the drop
  canopy(L)
  L.role('metal').chain([0, 0, -1.1], [0, 0, zb + 30.4])
  // the strap crown: four bent straps to the hook loop
  L.ring([0, 0, zb + 30.2], 0.9, 0.2, 'x', 14, 6)
  const zc = zb + 25.0
  for (let k = 0; k < 4; k++) {
    const a = (Math.PI / 2) * k + Math.PI / 8
    const p = (r: number, z: number): V3 => [Math.cos(a) * r, Math.sin(a) * r, z]
    L.tube(LB.bez(p(4.4, zc), p(4.9, zc + 3.0), p(2.6, zb + 30.2), p(0.6, zb + 29.6), 12), 0.26, 4)
  }
  // the elongated octagon: its outline in elevation, revolved in 8 facets
  const prof: [number, number][] = [[3.4, 0], [9.4, 6.4], [9.4, 18.6], [4.4, 25.0]]
  const ang = (k: number) => Math.PI / 8 + (Math.PI * 2 * k) / 8
  const at = (r: number, z: number, k: number): V3 => [Math.cos(ang(k)) * r, Math.sin(ang(k)) * r, zb + z]
  L.role('metal')
  for (let k = 0; k < 8; k++) {
    for (let i = 0; i + 1 < prof.length; i++) L.bar(at(prof[i][0], prof[i][1], k), at(prof[i + 1][0], prof[i + 1][1], k), 0.3)
    for (const [r, z] of prof) L.bar(at(r, z, k), at(r, z, k + 1), 0.24)
    for (let i = 0; i + 1 < prof.length; i++) {
      L.pane(at(prof[i][0] - 0.1, prof[i][1], k), at(prof[i][0] - 0.1, prof[i][1], k + 1), at(prof[i + 1][0] - 0.1, prof[i + 1][1], k + 1), at(prof[i + 1][0] - 0.1, prof[i + 1][1], k))
    }
  }
  // the bottom: an octagonal plate and a hanging bud
  L.role('metal').latheAt([0, 0, zb], [[0, 0.3], [3.6, 0.3], [3.6, 0], [0, 0]], 8, Math.PI / 8)
  L.latheAt([0, 0, zb], [[0, 0], [0.8, 0], [1.0, -0.6], [0.4, -1.4], [0, -1.6]], 14)
  // three candles on a central stem
  L.cyl([0, 0, zb + 0.3], [0, 0, zb + 7.0], 0.32, 10)
  for (let k = 0; k < 3; k++) {
    const a = (Math.PI * 2 * k) / 3
    const p: V3 = [Math.cos(a) * 2.6, Math.sin(a) * 2.6, zb + 9.2]
    L.role('metal').tube([[0, 0, zb + 6.6], [Math.cos(a) * 1.6, Math.sin(a) * 1.6, zb + 7.0], [p[0], p[1], zb + 8.2], p], 0.2, 6)
    bobeche(L, p, 1.1)
    candle(L, p, 4.2, 0.5)
  }
}

function pendantCube(L: LB, zb: number) {
  canopy(L, 2.6, 1.1, true)
  L.role('metal').cyl([0, 0, -1], [0, 0, zb + 18.2], 0.4, 4)
  const h = 9.0, z0 = zb, z1 = zb + 18.0
  const b = 0.32
  // the twelve edges of the cube (square bars) + a top plate
  for (const x of [-h, h]) for (const y of [-h, h]) L.bar([x, y, z0], [x, y, z1], b)
  for (const z of [z0, z1]) {
    L.bar([-h, -h, z], [h, -h, z], b); L.bar([h, -h, z], [h, h, z], b)
    L.bar([h, h, z], [-h, h, z], b); L.bar([-h, h, z], [-h, -h, z], b)
  }
  L.box(-1.6, 1.6, -1.6, 1.6, z1 - 0.2, z1 + 0.35)
  // a cross of four candles on bars across the cube's mid height
  L.bar([-h, 0, zb + 5.5], [h, 0, zb + 5.5], 0.18); L.bar([0, -h, zb + 5.5], [0, h, zb + 5.5], 0.18)
  for (const [x, y] of [[4.2, 0], [-4.2, 0], [0, 4.2], [0, -4.2]] as [number, number][]) {
    bobeche(L, [x, y, zb + 5.9], 1.1)
    candle(L, [x, y, zb + 5.9], 3.8, 0.5)
  }
}

function pendantCage(L: LB, zb: number) {
  canopy(L); downrod(L, zb + 11.6)
  L.role('metal').latheAt([0, 0, zb + 9.4], [[0, 2.4], [0.75, 2.4], [0.85, 2.0], [0.85, 0], [0, 0]], 14)
  cageLamp(L, [0, 0, zb + 8.4], { hoodR: 6.1 })
}

function pendantSchoolhouse(L: LB, zb: number) {
  canopy(L); downrod(L, zb + 12.8)
  // the fitter (holder) with its rim band
  L.role('metal').latheAt([0, 0, zb + 9.6], [[0, 3.4], [0.8, 3.4], [1.6, 2.6], [2.4, 1.0], [2.45, 0.35], [2.25, 0], [0, 0]], 24)
  // the opal globe: neck, shoulder, the full belly, the rounded bottom
  L.role('opal').latheAt([0, 0, zb], [[2.1, 9.7], [2.3, 9.2], [3.4, 8.4], [4.6, 7.0], [5.2, 5.2], [5.0, 3.0], [4.1, 1.3], [2.7, 0.3], [1.2, 0], [0, 0]], 32)
}

function pendantCloche(L: LB, zb: number) {
  canopy(L, 2.4)
  L.role('cord').cyl([0, 0, -1], [0, 0, zb + 11.8], 0.18, 8)
  // the brass cap and the clear bell
  L.role('metal').latheAt([0, 0, zb + 10.6], [[0, 1.9], [0.6, 1.9], [1.2, 1.2], [1.6, 0.1], [1.55, 0], [0, 0]], 20)
  L.role('glass').latheAt([0, 0, zb], [[1.55, 10.7], [2.2, 10.3], [3.3, 8.8], [3.9, 6.4], [4.1, 3.0], [4.5, 0.8], [4.7, 0.15], [4.6, 0]], 32)
  edisonDown(L, [0, 0, zb + 10.6], 5.2)
}

function pendantDrum(L: LB, zb: number) {
  canopy(L); downrod(L, zb + 10.4)
  // the fabric drum (a shell: out, then in), its diffuser, the spider and finial
  L.role('fabric').latheAt([0, 0, zb], [[10.0, 10.6], [10.0, 0.4], [9.88, 0.4], [9.88, 10.6]], 48)
  L.role('metal').ring([0, 0, zb + 10.6], 10.0, 0.1, 'z', 48, 5)
  L.ring([0, 0, zb + 0.4], 10.0, 0.1, 'z', 48, 5)
  L.role('opal').latheAt([0, 0, zb + 0.4], [[0, 0.05], [9.85, 0.05], [9.85, 0], [0, 0]], 40)
  L.role('metal').latheAt([0, 0, zb + 0.4], [[0, 0], [0.7, 0], [0.9, -0.4], [0.4, -0.4], [0, -0.4]], 14)
  for (let k = 0; k < 3; k++) {
    const a = (Math.PI * 2 * k) / 3
    L.cyl([0, 0, zb + 10.0], [Math.cos(a) * 9.9, Math.sin(a) * 9.9, zb + 10.4], 0.12, 6)
  }
  L.latheAt([0, 0, zb + 9.6], [[0, 1.0], [1.1, 1.0], [1.1, 0], [0, 0]], 16)
  edisonDown(L, [0, 0, zb + 9.6], 4.6)
}

function pendantCone(L: LB, zb: number) {
  canopy(L, 2.4)
  L.role('cord').cyl([0, 0, -1], [0, 0, zb + 12.6], 0.16, 8)
  const prof: [number, number][] = [[0, 13.0], [0.9, 13.0], [1.25, 12.0], [5.9, 0.6], [6.15, 0], [5.95, 0.08], [1.1, 11.9], [0, 11.9]]
  L.role('metal').latheAt([0, 0, zb], prof.slice(0, 5), 36)
  L.role('enamel').latheAt([0, 0, zb], prof.slice(4), 36)
  edisonDown(L, [0, 0, zb + 11.9], 4.4)
}

// ======================================================================================
// CHANDELIERS
// ======================================================================================

/** one tier of S-curved candle arms around a column at zArm, cups at radius R, height up */
function candleArms(L: LB, n: number, zArm: number, R: number, up: number, phase = 0) {
  for (let k = 0; k < n; k++) {
    const a = phase + (Math.PI * 2 * k) / n
    const c = Math.cos(a), s = Math.sin(a)
    const p = (r: number, z: number): V3 => [c * r, s * r, z]
    L.role('metal').tube(LB.bez(p(1.0, zArm), p(R * 0.55, zArm - 3.4), p(R * 1.02, zArm - 0.6), p(R, zArm + up - 1.0), 18), 0.32, 8)
    // a small curl under each arm
    L.tube(LB.bez(p(R * 0.35, zArm - 2.2), p(R * 0.42, zArm - 4.4), p(R * 0.22, zArm - 4.6), p(R * 0.2, zArm - 3.4), 10), 0.18, 6)
    L.cyl(p(R, zArm + up - 1.1), p(R, zArm + up), 0.38, 10)
    bobeche(L, p(R, zArm + up), 1.6)
    candle(L, p(R, zArm + up), 4.2, 0.55)
  }
}
/** a turned iron column, bottom at z0, height h */
function column(L: LB, z0: number, h: number) {
  L.role('metal').latheAt([0, 0, z0], [
    [0, h], [0.6, h], [0.6, h - 1.2], [1.3, h - 1.6], [1.3, h - 2.1], [0.55, h - 2.5], [0.55, h * 0.62],
    [1.5, h * 0.55], [1.65, h * 0.48], [1.5, h * 0.42], [0.65, h * 0.36], [0.65, 3.2],
    [2.4, 2.4], [2.7, 1.6], [2.2, 0.8], [1.0, 0.2], [0.5, -0.8], [0.15, -2.0], [0, -2.2],
  ], 18)
}
function chandelierCandle(L: LB, zb: number, two: boolean) {
  canopy(L)
  const h = two ? 27.5 : 19.5
  L.role('metal').chain([0, 0, -1.1], [0, 0, zb + 2.2 + h + 0.8])
  L.ring([0, 0, zb + 2.2 + h + 0.4], 0.8, 0.18, 'x', 14, 6)
  column(L, zb + 2.2, h)
  if (two) {
    candleArms(L, 8, zb + 9.0, 15.0, 4.6)
    candleArms(L, 4, zb + 19.0, 8.0, 4.6, Math.PI / 4)
  } else {
    candleArms(L, 6, zb + 8.8, 13.0, 4.6)
  }
}
function chandelierRing(L: LB, zb: number, two: boolean) {
  canopy(L)
  const tiers: { z: number; R: number; n: number }[] = two
    ? [{ z: zb + 0.5, R: 16.4, n: 10 }, { z: zb + 9.9, R: 10.0, n: 6 }]
    : [{ z: zb + 0.5, R: 14.4, n: 8 }]
  const zHub = zb + (two ? 24.3 : 16.5)
  L.role('metal').chain([0, 0, -1.1], [0, 0, zHub + 0.4])
  L.ring([0, 0, zHub], 1.2, 0.3, 'z', 16, 6)
  for (const t of tiers) {
    // the hoop (a flat iron band) and its candles
    L.role('metal').ring([0, 0, t.z], t.R, 0.42, 'z', 64, 4)
    for (let k = 0; k < t.n; k++) {
      const a = (Math.PI * 2 * k) / t.n
      const p: V3 = [Math.cos(a) * t.R, Math.sin(a) * t.R, t.z + 0.4]
      bobeche(L, p, 1.5)
      candle(L, p, 4.0, 0.55)
      // a C-scroll inside the hoop between candles
      const b = a + Math.PI / t.n
      const q = (r: number, z: number): V3 => [Math.cos(b) * r, Math.sin(b) * r, z]
      L.role('metal').tube(LB.bez(q(t.R - 0.4, t.z), q(t.R - 3.2, t.z - 0.2), q(t.R - 3.4, t.z + 2.6), q(t.R - 1.6, t.z + 2.2), 10), 0.2, 6)
    }
    // three straps up to the hub
    for (let k = 0; k < 3; k++) {
      const a = (Math.PI * 2 * k) / 3 + Math.PI / 6
      L.role('metal').bar([Math.cos(a) * t.R, Math.sin(a) * t.R, t.z], [Math.cos(a) * 1.1, Math.sin(a) * 1.1, zHub], 0.22)
    }
  }
  L.role('metal').latheAt([0, 0, zHub], [[0, 0], [0.9, 0], [1.2, -1.4], [0.5, -2.6], [0, -3.0]], 14)
}

// ======================================================================================
// FLUSH MOUNTS (z 0 = the ceiling; fixed height)
// ======================================================================================

function flushDrum(L: LB) {
  const zb = -7.0
  L.role('metal').latheAt([0, 0, 0], [[0, 0], [3.0, 0], [3.0, -0.6], [0, -0.6]], 24)
  L.role('fabric').latheAt([0, 0, zb], [[8.0, 6.4], [8.0, 0.6], [7.88, 0.6], [7.88, 6.4]], 48)
  L.role('metal').ring([0, 0, zb + 6.4], 8.0, 0.1, 'z', 48, 5)
  L.ring([0, 0, zb + 0.6], 8.0, 0.1, 'z', 48, 5)
  L.role('opal').latheAt([0, 0, zb + 0.6], [[0, 0.05], [7.85, 0.05], [7.85, 0], [0, 0]], 40)
  L.role('metal').latheAt([0, 0, zb + 0.6], [[0, 0], [0.55, 0], [0.75, -0.3], [0.45, -0.6], [0, -0.6]], 14)
  L.cyl([0, 0, -0.6], [0, 0, zb + 0.6], 0.25, 8)
}
function flushCage(L: LB) {
  L.role('metal').latheAt([0, 0, 0], [[0, 0], [4.7, 0], [4.7, -0.55], [3.6, -1.0], [0, -1.0]], 28)
  L.latheAt([0, 0, -1.0], [[0, 0], [2.5, 0], [2.5, -0.9], [2.2, -1.05], [0, -1.05]], 24)
  L.role('glass').sphere([0, 0, -5.2], 3.0, 20)
  L.role('metal')
  L.ring([0, 0, -5.2], 3.3, 0.15, 'z', 28, 6)
  L.ring([0, 0, -7.2], 2.4, 0.13, 'z', 24, 6)
  for (let k = 0; k < 4; k++) {
    const a = (Math.PI / 2) * k + Math.PI / 4
    const pts: V3[] = []
    for (let i = 0; i <= 10; i++) {
      const t = -Math.PI / 2 + (Math.PI * 0.85 * i) / 10
      pts.push([Math.cos(a) * Math.max(Math.cos(t) * 3.3, 0.9), Math.sin(a) * Math.max(Math.cos(t) * 3.3, 0.9), -5.2 + Math.sin(t) * 3.3])
    }
    L.tube(pts, 0.14, 6, false)
  }
  L.latheAt([0, 0, -8.6], [[0, 0.3], [0.9, 0.3], [0.9, -0.2], [0.35, -0.6], [0, -0.75]], 14)
  edisonDown(L, [0, 0, -2.0], 4.0)
}
function flushSchoolhouse(L: LB) {
  L.role('metal').latheAt([0, 0, 0], [[0, 0], [3.2, 0], [3.2, -0.6], [1.0, -1.0], [0.6, -1.4], [0.6, -2.0], [0, -2.0]], 24)
  L.latheAt([0, 0, -5.2], [[0, 3.2], [0.8, 3.2], [1.6, 2.4], [2.4, 0.9], [2.45, 0.3], [2.25, 0], [0, 0]], 24)
  L.role('opal').latheAt([0, 0, -12.0], [[2.1, 6.9], [2.3, 6.5], [3.4, 5.9], [4.6, 4.7], [5.2, 3.2], [5.0, 1.9], [4.1, 0.9], [2.7, 0.2], [1.2, 0], [0, 0]], 32)
}

// ======================================================================================
// GROUND LIGHTS (z 0 = the base)
// ======================================================================================

/** a pier-top lantern: plinth, mullioned glass body, hip or pagoda roof */
function pierLantern(L: LB, pagoda: boolean) {
  L.role('metal')
  // the base: a stepped plinth with the flared foot of the body
  L.lathe([[0, 1.0], [6.0 * Math.SQRT2, 1.0], [6.0 * Math.SQRT2, 0], [0, 0]], 4, Math.PI / 4)
  L.lathe([[0, 1.8], [4.6 * Math.SQRT2, 1.8], [5.3 * Math.SQRT2, 1.0], [0, 1.0]], 4, Math.PI / 4)
  lanternBody(L, { zb: 1.8, zt: 14.0, hb: 4.2, ht: 4.2, post: 0.42, cols: 2, rows: 3 })
  hipRoof(L, 14.0, 5.6, pagoda ? 5.4 : 4.2, 1.1, pagoda)
  const zt = 14.0 + 0.45 + (pagoda ? 5.4 : 4.2)
  L.role('metal').latheAt([0, 0, zt], [[0, 0.9], [1.4 * Math.SQRT2, 0.9], [1.4 * Math.SQRT2, 0], [0, 0]], 4, Math.PI / 4)
  finial(L, [0, 0, zt + 0.9], 0.75, pagoda ? 1.4 : 0.9)
  // the candle-sleeve lamp on its holder
  L.role('metal').latheAt([0, 0, 1.8], [[0, 1.0], [1.3, 1.0], [1.6, 0.3], [1.6, 0], [0, 0]], 16)
  candle(L, [0, 0, 2.8], 4.2, 0.6)
}

/** a timber post with a cross arm and knee brace, a coach lantern hanging off the arm's end */
function postArm(L: LB) {
  const H = 96, armY = 28
  L.role('wood')
  L.box(-2.75, 2.75, -2.75, 2.75, 0, H)                                      // 6×6 post
  L.box(-3.0, 3.0, -3.0, 3.0, H, H + 1.2)                                    // cap
  L.box(-1.75, 1.75, -1.5, armY + 2.5, H - 9.0, H - 3.5)                     // cross arm (4×6)
  L.frame([0, 2.75, H - 24], rotX(Math.PI / 4), () => L.box(-1.6, 1.6, 0, 20, -1.6, 1.6))   // knee brace
  L.role('metal')
  L.latheAt([0, 0, H + 1.2], [[0, 2.4], [0.4, 2.4], [1.4, 1.0], [1.4, 0], [0, 0]], 4, Math.PI / 4)
  // the hook eye and the chain down to the lantern
  L.ring([0, armY, H - 10.2], 0.9, 0.22, 'x', 14, 6)
  L.chain([0, armY, H - 11.0], [0, armY, 82.8])
  L.ring([0, armY, 82.4], 0.8, 0.18, 'y', 14, 6)
  L.frame([0, armY, 58.0], ID, () => {
    L.role('metal')
    L.lathe([[0, 0], [4.4 * Math.SQRT2, 0], [4.4 * Math.SQRT2, -0.5], [1.0 * Math.SQRT2, -2.0], [0, -2.0]], 4, Math.PI / 4)
    lanternBody(L, { zb: 0, zt: 16.0, hb: 4.0, ht: 5.0, post: 0.34 })
    hipRoof(L, 16.0, 5.8, 4.0, 1.4)
    L.role('metal').latheAt([0, 0, 20.4], [[0, 1.4], [1.7 * Math.SQRT2, 1.4], [1.7 * Math.SQRT2, 1.0], [1.2 * Math.SQRT2, 1.0], [1.2 * Math.SQRT2, 0], [0, 0]], 4, Math.PI / 4)
    L.ring([0, 0, 23.6], 0.8, 0.2, 'x', 14, 6)
    L.latheAt([0, 0, 0], [[0, 1.4], [1.1, 1.4], [1.3, 0.4], [1.3, 0], [0, 0]], 16)
    candle(L, [0, 0, 1.4], 7.6, 0.6)
  })
}

function pathMushroom(L: LB) {
  L.role('metal')
  L.latheAt([0, 0, 0], [[0, 1.2], [1.6, 1.2], [2.0, 0.5], [2.0, 0], [0, 0]], 20)          // base / stake collar
  L.cyl([0, 0, 1.0], [0, 0, 18.6], 0.48, 12)                                           // stem
  L.latheAt([0, 0, 18.4], [[0, 1.2], [0.9, 1.2], [1.1, 0.4], [0.6, 0], [0, 0]], 14)      // knuckle
  // the hat: domed top, a turned-down rim, the lens under it
  L.latheAt([0, 0, 19.0], [[0, 2.6], [1.4, 2.5], [3.0, 1.8], [4.2, 0.6], [4.6, -0.1], [4.5, -0.3], [4.1, 0.1], [2.8, 0.9], [1.0, 1.3], [0, 1.3]], 36)
  L.role('bulb').latheAt([0, 0, 19.0], [[0, 1.3], [1.6, 1.3], [1.6, 0.5], [0, 0.5]], 20)
}

function pathBollard(L: LB) {
  L.role('metal')
  L.latheAt([0, 0, 0], [[0, 0.8], [3.2, 0.8], [3.2, 0], [0, 0]], 28)
  L.cyl([0, 0, 0.8], [0, 0, 23.0], 2.75, 28)
  // the louvered head: three rings with the lens between, and the cap
  for (const z of [23.0, 25.0, 27.0]) L.latheAt([0, 0, z], [[0, 0.5], [2.75, 0.5], [3.15, -0.1], [2.75, -0.1], [0, -0.1]], 28)
  L.role('bulb').cyl([0, 0, 23.0], [0, 0, 29.0], 2.3, 24)
  L.role('metal').latheAt([0, 0, 29.0], [[0, 1.6], [1.4, 1.6], [2.8, 0.9], [3.0, 0.2], [2.75, 0], [0, 0]], 28)
}

// ======================================================================================
// the proto
// ======================================================================================

/** build the style's mesh into a fresh kit (exported for the extents test) */
export function buildLightStyleKit(kind: string, style: LightStyle, dropIn: number): LB {
  const L = new LB()
  const zb = -dropIn
  switch (`${kind}:${style.value}`) {
    case 'sconce:barnDome': sconceBarn(L, false); break
    case 'sconce:barnCone': sconceBarn(L, true); break
    case 'sconce:coach': sconceCoach(L); break
    case 'sconce:gas': sconceGas(L); break
    case 'sconce:box': sconceBox(L); break
    case 'sconce:bulkhead': sconceBulkhead(L); break
    case 'sconce:library': sconceLibrary(L); break
    case 'sconce:halfBowl': sconceHalfBowl(L); break
    case 'sconce:picture': sconcePicture(L); break
    case 'stepLight:louver': stepLouver(L); break
    case 'stepLight:eyelid': stepEyelid(L); break
    case 'pendant:warehouse': pendantWarehouse(L, zb); break
    case 'pendant:lantern': pendantLantern(L, zb); break
    case 'pendant:octagon': pendantOctagon(L, zb); break
    case 'pendant:cube': pendantCube(L, zb); break
    case 'pendant:cage': pendantCage(L, zb); break
    case 'pendant:schoolhouse': pendantSchoolhouse(L, zb); break
    case 'pendant:cloche': pendantCloche(L, zb); break
    case 'pendant:drum': pendantDrum(L, zb); break
    case 'pendant:cone': pendantCone(L, zb); break
    case 'chandelier:candleArm': chandelierCandle(L, zb, false); break
    case 'chandelier:candleArm2': chandelierCandle(L, zb, true); break
    case 'chandelier:ring': chandelierRing(L, zb, false); break
    case 'chandelier:ring2': chandelierRing(L, zb, true); break
    case 'flushMount:drum': flushDrum(L); break
    case 'flushMount:cage': flushCage(L); break
    case 'flushMount:schoolhouse': flushSchoolhouse(L); break
    case 'postLantern:pierHip': pierLantern(L, false); break
    case 'postLantern:pierPagoda': pierLantern(L, true); break
    case 'postLantern:postArm': postArm(L); break
    case 'pathLight:mushroom': pathMushroom(L); break
    case 'pathLight:bollard': pathBollard(L); break
    default: break
  }
  return L
}

export function buildLightStyleProto(el: ResolvedElement, key: string): OpeningProto {
  const tp = el.typeParams as Record<string, ParamValue>
  const kind = String(tp.fixtureKind || '')
  const merged = { ...tp, ...el.params }
  const style = lightStyleOf(kind, merged)!
  const dropIn = kind === 'pendant' || kind === 'chandelier' ? lightDropFt(style, merged) * 12 : 0
  const L = buildLightStyleKit(kind, style, dropIn)
  const b = new MeshBuilder()
  const bodyMat = lightBodyMaterialId(style.body)
  const finish = el.memberColor || style.finish
  const groups = L.finish(b, (role) => role === 'metal' ? tintMaterialId(finish, bodyMat) : tintMaterialId(ROLE_HEX[role] || '#cccccc', 'default'))
  // SLOPED CEILINGS (ceilingTilt.ts): a ceiling light square to the ceiling — a hanging one
  // only at its canopy, its rod or chain and body plumb
  const tilt = ceilingTiltOf(el.params as Record<string, ParamValue>)
  if (tilt) { tiltForCeiling(b, tilt, kind); if (L.glass.vertCount) tiltForCeiling(L.glass, tilt, kind) }
  const solid = b.build()
  const glass = L.glass.vertCount ? L.glass.build() : null
  return { key, solid, glass, edges: featureEdges(solid), solidMaterialId: null, solidGroups: groups }
}

/** the kit's mesh extents in inches (for the catalog test) */
export function lightKitExtentsIn(L: LB): { min: V3; max: V3 } {
  const b = new MeshBuilder()
  L.finish(b, () => null)
  const g = L.glass
  const min: V3 = [Infinity, Infinity, Infinity], max: V3 = [-Infinity, -Infinity, -Infinity]
  for (const arr of [b.pos, g.pos]) for (let i = 0; i < arr.length; i += 3) for (let k = 0; k < 3; k++) {
    const v = arr[i + k] * 12
    if (v < min[k]) min[k] = v
    if (v > max[k]) max[k] = v
  }
  return { min, max }
}
